import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, PromoBatchCode } from '../api';
import { promotionEndpoints } from '../api';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT } from './event/shared';

/**
 * Batches of single-use codes on a promotion (D-359).
 *
 * Kept off the shared-codes table on purpose: a batch is hundreds of codes,
 * and the one shared code somebody came to retire would be lost among them.
 * Each batch opens to its own list, with every code's status and the order
 * that used it, and downloads as a spreadsheet for the mail merge.
 */

const STATUS: Record<PromoBatchCode['status'], { label: string; tone: Tone }> = {
  UNUSED: { label: 'Unused', tone: 'neutral' },
  IN_CHECKOUT: { label: 'In a checkout', tone: 'accent' },
  PARTLY_USED: { label: 'Partly used', tone: 'warn' },
  USED: { label: 'Used', tone: 'ok' },
  DISABLED: { label: 'Disabled', tone: 'bad' },
};

/** "Name, email" per line; either half may be missing. */
function parseRecipients(text: string): { name: string | null; email: string | null }[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[,\t]/).map((part) => part.trim());
      const email = parts.find((part) => part.includes('@')) ?? null;
      const name = parts.filter((part) => part !== email).join(' ').trim() || null;
      return { name, email };
    });
}

export function PromoCodeBatches({ promotionId, onChanged }: { promotionId: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', quantity: '100', prefix: '', length: '8', uses: '1', recipients: '' });
  const batches = useQuery({ queryKey: ['promotion-batches', promotionId], queryFn: () => promotionEndpoints.batches(promotionId) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['promotion-batches', promotionId] });
    void queryClient.invalidateQueries({ queryKey: ['promotion-batch'] });
    onChanged();
  };
  const fail = (cause: ApiError) => setError(cause.message);

  const create = useMutation({
    mutationFn: () => {
      const recipients = parseRecipients(form.recipients);
      return promotionEndpoints.createBatch(promotionId, {
        name: form.name.trim(),
        ...(recipients.length > 0 ? { recipients } : { quantity: Number(form.quantity) }),
        prefix: form.prefix.trim(),
        length: Number(form.length),
        uses_per_code: Number(form.uses),
      });
    },
    onSuccess: (result) => {
      setError(null);
      setForm({ name: '', quantity: '100', prefix: '', length: '8', uses: '1', recipients: '' });
      setOpen(result.batch.id);
      refresh();
    },
    onError: fail,
  });
  const disable = useMutation({ mutationFn: (batchId: string) => promotionEndpoints.disableBatch(promotionId, batchId), onSuccess: refresh, onError: fail });
  const recipientCount = parseRecipients(form.recipients).length;

  return (
    <Panel
      title="Single-use codes"
      description="A batch of unique codes, each good for one order (or the number you choose). Hand them out one per person; each is used up when an order with it is paid, and comes back if that checkout is abandoned."
    >
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <form
        className="flex flex-wrap items-end gap-3 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Batch name" htmlFor="batch-name">
          <input id="batch-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="WXYZ morning show" required maxLength={80} />
        </Field>
        <Field label="How many" htmlFor="batch-quantity" hint={recipientCount > 0 ? `One per recipient: ${recipientCount}` : 'Up to 5,000'}>
          <input id="batch-quantity" type="number" min={1} max={5000} className={INPUT} value={recipientCount > 0 ? String(recipientCount) : form.quantity} disabled={recipientCount > 0} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
        </Field>
        <Field label="Prefix" htmlFor="batch-prefix" hint="Optional, e.g. WXYZ-">
          <input id="batch-prefix" className={INPUT} value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} maxLength={12} pattern="[A-Za-z0-9][A-Za-z0-9-]*" />
        </Field>
        <Field label="Random characters" htmlFor="batch-length" hint="No 0, O, 1, I or L">
          <input id="batch-length" type="number" min={6} max={16} className={INPUT} value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} />
        </Field>
        <Field label="Orders per code" htmlFor="batch-uses">
          <input id="batch-uses" type="number" min={1} max={100} className={INPUT} value={form.uses} onChange={(e) => setForm({ ...form, uses: e.target.value })} />
        </Field>
        <div className="w-full">
          <Field label="Recipients (optional)" htmlFor="batch-recipients" hint="One per line: name, email. Each gets their own code, and the spreadsheet lists who has which.">
            <textarea id="batch-recipients" rows={3} className={`${INPUT} w-full max-w-xl`} value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder={'Ada Lovelace, ada@example.com'} />
          </Field>
        </div>
        <Button type="submit" variant="primary" disabled={create.isPending || !form.name.trim()}>
          {create.isPending ? 'Making codes…' : 'Make codes'}
        </Button>
      </form>
      <Table headers={['Batch', 'Codes', 'Used', 'In a checkout', 'Disabled', 'Made', '']}>
        {(batches.data?.batches ?? []).length === 0 ? (
          <EmptyRow columns={7}>No batches yet.</EmptyRow>
        ) : (
          batches.data!.batches.map((batch) => (
            <tr key={batch.id} className="border-b border-line align-top last:border-0">
              <td className="px-5 py-3 text-sm">
                <button type="button" className="font-medium text-ink-900 hover:underline" onClick={() => setOpen(open === batch.id ? null : batch.id)}>
                  {batch.name}
                </button>
                <div className="text-xs text-ink-400">
                  {batch.prefix}
                  {'X'.repeat(batch.code_length)} · {batch.uses_per_code === 1 ? 'one order each' : `${batch.uses_per_code} orders each`}
                  {batch.disabled_at ? ` · disabled ${formatWhen(batch.disabled_at)}` : ''}
                </div>
                {open === batch.id ? <BatchCodes promotionId={promotionId} batchId={batch.id} onChanged={refresh} onError={fail} /> : null}
              </td>
              <td className="px-5 py-3 text-sm tabular">{batch.codes}</td>
              <td className="px-5 py-3 text-sm tabular">{batch.used_codes}</td>
              <td className="px-5 py-3 text-sm tabular">{batch.in_checkout}</td>
              <td className="px-5 py-3 text-sm tabular">{batch.disabled_codes}</td>
              <td className="px-5 py-3 text-sm whitespace-nowrap">{formatWhen(batch.created_at)}</td>
              <td className="space-x-2 px-5 py-3 text-right text-sm whitespace-nowrap">
                <a href={promotionEndpoints.batchCsvUrl(promotionId, batch.id)} className="text-brand hover:underline">
                  Download CSV
                </a>
                {batch.disabled_codes < batch.codes ? (
                  <Button
                    onClick={() => {
                      if (window.confirm(`Disable every code in ${batch.name}? Codes already in somebody's checkout still complete; nobody can start a new one with them.`)) disable.mutate(batch.id);
                    }}
                    disabled={disable.isPending}
                  >
                    Disable batch
                  </Button>
                ) : null}
              </td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
  );
}

function BatchCodes({ promotionId, batchId, onChanged, onError }: { promotionId: string; batchId: string; onChanged: () => void; onError: (cause: ApiError) => void }) {
  const detail = useQuery({ queryKey: ['promotion-batch', batchId], queryFn: () => promotionEndpoints.batch(promotionId, batchId) });
  const toggle = useMutation({ mutationFn: (input: { codeId: string; active: boolean }) => promotionEndpoints.updateCode(promotionId, input.codeId, { active: input.active }), onSuccess: onChanged, onError });
  if (!detail.data) return <p className="mt-2 text-xs text-ink-400">Loading…</p>;
  return (
    <div className="mt-3 max-h-96 overflow-y-auto rounded border border-line">
      <table className="w-full text-xs">
        <thead className="bg-page text-left text-ink-400">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Recipient</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Order</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {detail.data.codes.map((code) => (
            <tr key={code.id} className="border-t border-line">
              <td className="px-3 py-1.5 font-mono">{code.code}</td>
              <td className="px-3 py-1.5">{[code.recipient_name, code.recipient_email].filter(Boolean).join(' · ') || '—'}</td>
              <td className="px-3 py-1.5">
                <Badge tone={STATUS[code.status].tone}>{STATUS[code.status].label}</Badge>
                {code.uses_allowed > 1 ? <span className="ml-1 text-ink-400">{`${code.used} of ${code.uses_allowed}`}</span> : null}
              </td>
              <td className="px-3 py-1.5 font-mono">{code.order_numbers.join(', ') || '—'}</td>
              <td className="px-3 py-1.5 text-right">
                {code.status === 'USED' ? null : (
                  <button type="button" className="text-ink-400 underline" onClick={() => toggle.mutate({ codeId: code.id, active: code.active !== 1 })}>
                    {code.active ? 'Disable' : 'Enable'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
